import React from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import ProductThumb from './ProductThumb';
import { AskedBadge, Chip, ColorDot, HeatBadge, SeasonBadge } from './Badges';
import { SKIP_LABEL } from './exclusions';
import { displayTitle, isGarmentFixture, moveHeadline, season, thumbTarget } from './format';
import { C, MONO, SHADOW, VERB } from './tokens';
import { RestockV2Move, RestockV2ThumbTarget } from './types';

interface MoveCardProps {
  move: RestockV2Move;
  done: boolean;
  pending: boolean;
  whyOpen: boolean;
  arrivalLabel?: string;
  // 今天不做: the owner (or the part-timer he told) takes this ONE move out of today's walk. Absent
  // only where a card is shown outside the walk.
  skipPending?: boolean;
  onDone: (move: RestockV2Move) => void;
  onUndo: (move: RestockV2Move) => void;
  onSkip?: (move: RestockV2Move) => void;
  onWhy: (id: string) => void;
  onImage: (target: RestockV2ThumbTarget) => void;
}

// One physical thing to do, as a part-timer reads it, top to bottom:
//   photo (84×112 — big enough to match a garment on the rail from arm's length)
//   verb pill + ×qty        → what to do, how many
//   colour · size           → how you find it (product name instead when there is no colour)
//   place chip, 門市有 N, 位置 → where
//   SKU + name              → the fallback identity, small
//   為什麼                   → the engine's sentence, folded away
//   做好了 · 今天不做          → one big tap to do it, one small one to take it out of today
//
// There is deliberately NO 不要這款 / 不要這個顏色／尺寸 here. 今天 is the part-timers' walk screen —
// staff EXECUTE the list, they do not decide it (Magnus's own framing for it: 「讓員工不用自己猜要選
// 什麼，照著清單就知道門市要留什麼、拿什麼、訂什麼、帶回什麼」). 今天不做 is the right authority for
// this card: day-scoped, gone overnight. A permanent assortment veto belongs on the rack tiles and
// in 選品, where Magnus plans. One was added here on 2026-09-17 and taken straight back out — it is
// the same class of thing that got 全選／全部取消 pulled off 訂貨 on 09-15.
// 復原 on a finished card is a REAL undo (POST /restock-v2/undo): 帶回了 moved stock, so unticking
// alone would have left the card lying about the inventory (Magnus 2026-09-15).
const MoveCard: React.FC<MoveCardProps> = ({
  move, done, pending, whyOpen, arrivalLabel, skipPending, onDone, onUndo, onSkip, onWhy, onImage,
}) => {
  const verb = VERB[move.kind];
  const line = move.line;
  // 等到貨 rows whose pieces all belong to a 門市訂單: 出貨人員 unpack that parcel on the receiving
  // page, and the counter's own receipt path cannot stamp those rows (it is scoped to
  // `batch_id IS NULL`). Offering 收到 here would tick the card green and change nothing — measured
  // on prod 2026-09-18. Same 「not yours to do yet」 treatment as an on_arrival move.
  const warehouseReceipt = move.kind === 'waiting' && line.warehouse_receipt === true;
  const locked = move.kind === 'on_arrival' || warehouseReceipt;
  const garment = isGarmentFixture(move.fixtureId);
  const headline = moveHeadline(line, garment);
  const withColour = !!line.color;
  const seasonKey = season(line.season);

  return (
    <Box
      component="article" data-move={move.id}
      sx={{
        border: '1px solid', borderColor: C.line, borderLeft: '4px solid', borderLeftColor: verb.colour,
        borderRadius: '10px', bgcolor: done ? C.wash : C.card, p: 1, boxShadow: done ? 'none' : SHADOW,
        opacity: done ? 0.75 : 1,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.25 }}>
        <Box sx={{ filter: done ? 'grayscale(1)' : 'none', flex: 'none' }}>
          <ProductThumb target={thumbTarget(line)} width={84} height={112} radius={7} onOpen={onImage} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              component="span"
              sx={{
                fontFamily: MONO, fontSize: 10.5, fontWeight: 700, color: '#fff', bgcolor: verb.colour,
                borderRadius: '4px', px: '5px', py: '2px', flex: 'none',
              }}
            >
              {verb.mark}
            </Box>
            <Typography component="span" sx={{ fontSize: 15.5, fontWeight: 800, letterSpacing: '-.01em' }}>
              {verb.label}
            </Typography>
            <Typography
              component="span"
              sx={{ ml: 'auto', fontFamily: MONO, fontSize: 21, fontWeight: 700, letterSpacing: '-.04em' }}
            >
              ×{line.qty}
            </Typography>
          </Box>

          <Typography
            component="div"
            sx={{
              mt: '3px', fontWeight: 900, letterSpacing: '-.02em',
              fontSize: withColour ? 20 : 16.5, lineHeight: withColour ? 1.2 : 1.28,
              textDecoration: done ? 'line-through' : 'none',
              ...(withColour ? {} : {
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }),
            }}
          >
            <ColorDot name={line.color} size={15} />
            {headline}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', mt: 0.75, mb: 0.5 }}>
            <Chip tone="place">{move.fixtureLabel}</Chip>
            {locked && arrivalLabel && !warehouseReceipt && <Chip tone="wait">{arrivalLabel} 到貨後</Chip>}
            {warehouseReceipt && <Chip tone="wait">門市訂單 · 收貨頁</Chip>}
            {move.kind === 'display' && line.on_hand > 0 && <Chip>門市有 {line.on_hand} 件</Chip>}
            {move.kind === 'from_office' && line.office > 0 && <Chip>辦公室有 {line.office} 件</Chip>}
            {/* Design A: a HOT/WARM 去拿 says how many hang and how many go into 櫃位. */}
            {move.kind === 'from_office' && (line.storage_qty || 0) > 0 && (
              <Chip>{(line.rail_qty || 0) > 0 ? `上架 ${line.rail_qty}＋` : ''}放櫃位 {line.storage_qty}</Chip>
            )}
            {move.kind === 'from_office' && <HeatBadge level={line.heat} />}
            {line.storage_slot && <Chip>位置 {line.storage_slot}</Chip>}
            {seasonKey && <SeasonBadge value={seasonKey} />}
            <AskedBadge asked={line.asked} />
          </Box>

          <Box sx={{ display: 'flex', gap: '5px', alignItems: 'baseline', fontSize: 11, color: C.ink3, minWidth: 0 }}>
            <Box
              component="span"
              sx={{
                fontFamily: MONO, fontWeight: 600, bgcolor: C.paper2, color: C.ink2, borderRadius: '3px',
                px: '4px', flex: 'none',
              }}
            >
              {line.sku}
            </Box>
            {withColour && (
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayTitle(line.title)}
              </Box>
            )}
          </Box>

          <Box
            component="button" type="button" onClick={() => onWhy(move.id)}
            aria-expanded={whyOpen} aria-label={`為什麼要動 ${line.sku}`}
            sx={{
              appearance: 'none', background: 'none', border: 0, p: '5px 0 0', fontSize: 11.5,
              color: C.ink3, fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '2px',
              cursor: 'pointer',
            }}
          >
            為什麼
          </Box>
          <Collapse in={whyOpen} unmountOnExit>
            <Box
              sx={{
                mt: '5px', fontSize: 12, color: C.ink2, bgcolor: C.wash, border: '1px dashed',
                borderColor: C.line2, borderRadius: '7px', px: 1, py: 0.75,
              }}
            >
              {line.reason || '這次沒有附理由。'}
              {line.lane_reason && <Box component="div" sx={{ mt: '3px', color: C.ink3 }}>{line.lane_reason}</Box>}
              {typeof line.listed_days === 'number' && (
                <Box component="div" sx={{ mt: '3px', color: C.ink3 }}>上架 {line.listed_days} 天</Box>
              )}
            </Box>
          </Collapse>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.75, mt: 1.25 }}>
        <Box
          component="button" type="button"
          disabled={pending || (locked && !done)}
          onClick={() => (done ? onUndo(move) : onDone(move))}
          sx={{
            flex: 1, minWidth: 0, border: 0, borderRadius: '9px', cursor: 'pointer',
            fontWeight: 800, letterSpacing: '.02em',
            minHeight: done || locked ? 38 : 46,
            fontSize: done || locked ? 13 : 15,
            bgcolor: locked && !done ? C.waitWash : (done ? 'transparent' : verb.colour),
            color: locked && !done ? C.wait : (done ? C.ink2 : '#FBF6EC'),
            ...(done ? { border: '1px solid', borderColor: C.line2 } : {}),
            '&:disabled': { cursor: 'default', opacity: pending ? 0.6 : 1 },
          }}
        >
          {done ? '已完成 · 復原' : warehouseReceipt ? '出貨人員收貨' : verb.action}
        </Box>
        {!done && onSkip && (
          <Box
            component="button" type="button" disabled={!!skipPending || pending}
            onClick={() => onSkip(move)} aria-label={`${SKIP_LABEL} ${line.sku}`}
            sx={{
              flex: 'none', px: 1.25, minHeight: locked ? 38 : 46, cursor: 'pointer',
              borderRadius: '9px', border: '1px solid', borderColor: C.line2, bgcolor: C.card,
              color: C.ink2, fontWeight: 800, fontSize: 12.5,
              '&:disabled': { cursor: 'default', opacity: 0.5 },
            }}
          >
            {SKIP_LABEL}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MoveCard;
